import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { format, parseISO } from 'date-fns';

const SessionList = ({ sessions, competitionColors }) => {
  // Debug: Log what we're receiving
  // console.log('SessionList sessions:', sessions);
  if (sessions?.length > 0) {
    // console.log('First session timeBlocks:', sessions[0]?.timeBlocks);
  }
  
  const [sortField, setSortField] = useState('sessionNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    setSortField(field);
    setSortDirection(current => current === 'asc' ? 'desc' : 'asc');
  };

  // Extract date from session timeBlocks
  const getSessionDate = (session) => {
    // Try to get date from competitionStart or any available time field
    const dateString = session.timeBlocks?.competitionStart || 
                      session.timeBlocks?.warmUpStart || 
                      session.timeBlocks?.marchInStart;
    
    if (!dateString) return null;
    
    try {
      // Handle MM/DD/YYYY HH:MM:SS format
      if (dateString.includes('/') && dateString.includes(':')) {
        const [datePart] = dateString.split(' ');
        const [month, day, year] = datePart.split('/');
        return new Date(year, month - 1, day);
      }
      // Handle YYYY-MM-DD HH:MM:SS format
      const isoString = dateString.replace(' ', 'T');
      const parsedDate = parseISO(isoString);
      if (!isNaN(parsedDate.getTime())) {
        return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
      }
    } catch (error) {
      console.error('Error parsing session date:', error);
    }
    return null;
  };

  const sortedSessions = [...sessions].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    if (typeof a[sortField] === 'string') {
      return direction * a[sortField].localeCompare(b[sortField]);
    }
    return direction * (a[sortField] - b[sortField]);
  });

  const formatTime = (dateString) => {
    if (!dateString) return '-';

    try {
      // Handle MM/DD/YYYY HH:MM:SS format like "01/23/2026 08:20:00"
      if (dateString.includes('/') && dateString.includes(':')) {
        const [datePart, timePart] = dateString.split(' ');
        const [month, day, year] = datePart.split('/');
        const [hours, minutes, seconds] = timePart.split(':');
        
        // Create date object (month is 0-indexed in JS Date)
        const date = new Date(year, month - 1, day, hours, minutes, seconds || 0);
        if (!isNaN(date.getTime())) {
          // Use custom formatting for consistent width
          let h = date.getHours();
          const m = date.getMinutes();
          const ampm = h >= 12 ? 'PM' : 'AM';
          h = h % 12 || 12;
          // Pad single digit hours with space for alignment
          const timeStr = `${h.toString().padStart(2, ' ')}:${m.toString().padStart(2, '0')} ${ampm}`;
          return timeStr;
        }
      }
      
      // Handle YYYY-MM-DD HH:MM:SS format like "2025-01-04 11:00:00"
      // Convert to ISO format by replacing space with T
      const isoString = dateString.replace(' ', 'T');
      const parsedDate = parseISO(isoString);
      
      if (!isNaN(parsedDate.getTime())) {
        let h = parsedDate.getHours();
        const m = parsedDate.getMinutes();
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        const timeStr = `${h.toString().padStart(2, ' ')}:${m.toString().padStart(2, '0')} ${ampm}`;
        return timeStr;
      }
      
      // Handle time strings like "5:00 PM"
      const timeParts = dateString.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (timeParts) {
        const [_, hours, minutes, period] = timeParts;
        const h = parseInt(hours);
        const timeStr = `${h.toString().padStart(2, ' ')}:${minutes} ${period.toUpperCase()}`;
        return timeStr;
      }
    } catch (error) {
      console.error('Error formatting time:', error, dateString);
    }
    return '-';
  };

  const hasScheduleData = (session) => {
    return session?.timeBlocks && (
      session.timeBlocks.warmUpStart ||
      session.timeBlocks.marchInStart ||
      session.timeBlocks.openStretchStart ||
      session.timeBlocks.competitionStart ||
      session.timeBlocks.awardsEnd
    );
  };

  const renderTableHeader = () => (
    <TableHead>
      <TableRow sx={{ backgroundColor: `${competitionColors.primary}15` }}>
        <TableCell sx={{ width: '100px', padding: '8px' }}>
          Date
        </TableCell>
        <TableCell onClick={() => handleSort('sessionNumber')} sx={{ cursor: 'pointer', width: '60px', padding: '8px', textAlign: 'center' }}>
          #
        </TableCell>
        <TableCell onClick={() => handleSort('type')} sx={{ cursor: 'pointer' }}>
          Type
        </TableCell>
        <TableCell>Levels</TableCell>
        <TableCell onClick={() => handleSort('gymnastCount')} sx={{ cursor: 'pointer', width: '50px', textAlign: 'center' }}>
          Total
        </TableCell>
        <TableCell sx={{ padding: '8px' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ width: '80px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.08)', pr: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                Warm Up
              </Typography>
            </Box>
            <Box sx={{ width: '80px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.08)', pr: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                March In
              </Typography>
            </Box>
            <Box sx={{ width: '90px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.08)', pr: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                Open Stretch
              </Typography>
            </Box>
            <Box sx={{ width: '80px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.08)', pr: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600 }}>
                Start
              </Typography>
            </Box>
            <Box sx={{ width: '80px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.08)', pr: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600 }}>
                End
              </Typography>
            </Box>
            <Box sx={{ width: '80px', textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                Awards End
              </Typography>
            </Box>
          </Box>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const renderSessionRow = (session, index, allSessions) => {
    const sessionDate = getSessionDate(session);
    const prevSessionDate = index > 0 ? getSessionDate(allSessions[index - 1]) : null;
    const showDate = !prevSessionDate || 
                     (sessionDate && prevSessionDate && 
                      sessionDate.toDateString() !== prevSessionDate.toDateString());

    return (
      <TableRow 
        key={session.sessionNumber}
        sx={{ 
          '&:nth-of-type(even)': {
            backgroundColor: 'rgba(255, 255, 255, 0.02)'
          },
          '&:hover': { 
            backgroundColor: `${competitionColors.secondary}20`,
            transition: 'background-color 0.2s'
          }
        }}
      >
        <TableCell sx={{ padding: '8px' }}>
          {showDate && sessionDate ? (
            <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
              {format(sessionDate, 'EEE M/d')}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell sx={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{session.sessionNumber}</TableCell>
        <TableCell>
                <Chip 
                  label={session.type}
                  size="small"
                  sx={{ 
                    backgroundColor: competitionColors.primary,
                    color: 'white'
                  }}
                />
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                  {session?.levels?.length > 0 ? session.levels.map((level) => (
                    <Chip 
                      key={level}
                      label={level.replace(/^Level\s+/i, '')}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderColor: competitionColors.secondary,
                        minWidth: '32px',
                        height: '20px',
                        fontSize: '0.75rem',
                        '& .MuiChip-label': {
                          px: 0.25,
                          py: 0,
                          lineHeight: 1
                        }
                      }}
                    />
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      No levels available
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>{session.gymnastCount}</TableCell>
              <TableCell sx={{ padding: '8px' }}>
                {hasScheduleData(session) ? (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ width: '80px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.05)', pr: 1 }}>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit', fontStyle: 'italic' }}>
                        {session.timeBlocks?.warmUpStart ? formatTime(session.timeBlocks.warmUpStart) : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '80px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.05)', pr: 1 }}>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit', fontStyle: 'italic' }}>
                        {session.timeBlocks?.marchInStart ? formatTime(session.timeBlocks.marchInStart) : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '90px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.05)', pr: 1 }}>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit', fontStyle: 'italic' }}>
                        {session.timeBlocks?.openStretchStart ? formatTime(session.timeBlocks.openStretchStart) : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '80px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.05)', pr: 1 }}>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit', fontWeight: 600 }}>
                        {session.timeBlocks?.competitionStart ? formatTime(session.timeBlocks.competitionStart) : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '80px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,0.05)', pr: 1 }}>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit', fontWeight: 600 }}>
                        {session.timeBlocks?.competitionEnd ? formatTime(session.timeBlocks.competitionEnd) : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '80px', textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit', fontStyle: 'italic' }}>
                        {session.timeBlocks?.awardsEnd ? formatTime(session.timeBlocks.awardsEnd) : '-'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.disabled" fontStyle="italic">
                    No schedule set
                  </Typography>
                )}
              </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        {renderTableHeader()}
        <TableBody>
          {sortedSessions.map((session, index) => renderSessionRow(session, index, sortedSessions))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SessionList;
